import { useCallback, useEffect, useMemo, useRef } from 'react';
import { logout } from '@/utils/logout';
import { getAccessToken } from '@/utils/accessToken';
import LocalStorage from '@/service/LocalStorage';
import throttle from 'lodash/throttle';

export const useInactivityLogout = (timeoutInMs = 30 * 60 * 1000) => {
  const timer = useRef<NodeJS.Timeout | null>(null);

  const logoutHandler = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;

    // technically not needed with storage listener but this ensures
    // we don't logout if another tab was active in case the
    // storage event listener is not supported in certain browsers
    const lastActivity = Number(LocalStorage.get('last_activity') || 0);
    const inactiveTime = Date.now() - lastActivity;

    if (inactiveTime >= timeoutInMs) {
      const isOnEmailLoginPage = window.location.pathname.includes('email-login');
      logout({ withRedirect: !isOnEmailLoginPage });
    } else {
      const remainingTime = Math.max(0, timeoutInMs - inactiveTime);
      timer.current = setTimeout(logoutHandler, remainingTime);
    }
  }, [timeoutInMs]);

  const resetTimer = useCallback(() => {
    const token = getAccessToken({ withLocalStorage: true });
    if (!token) {
      return;
    }

    const now = Date.now();
    LocalStorage.set('last_activity', now.toString());

    if (timer.current) {
      clearTimeout(timer.current);
    }
    timer.current = setTimeout(logoutHandler, timeoutInMs);
  }, [logoutHandler, timeoutInMs]);

  const throttledResetTimer = useMemo(
    () =>
      throttle(() => resetTimer(), 10000, {
        leading: true,
        trailing: false,
      }),
    [resetTimer]
  );

  useEffect(() => {
    const token = getAccessToken({ withLocalStorage: true });
    if (!token) {
      return;
    }

    const lastActivity = Number(LocalStorage.get('last_activity') || Date.now());
    const inactiveTime = Date.now() - lastActivity;

    if (inactiveTime >= timeoutInMs) {
      logoutHandler();
      return;
    }

    const remainingTime = Math.max(0, timeoutInMs - inactiveTime);
    timer.current = setTimeout(logoutHandler, remainingTime);

    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click', 'keydown'];
    events.forEach((event) => {
      window.addEventListener(event, throttledResetTimer, false);
    });

    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === LocalStorage.createKey('last_activity')) {
        if (event.newValue) {
          // resetting timer when activity is detected in another tab
          if (timer.current) clearTimeout(timer.current);
          timer.current = setTimeout(logoutHandler, timeoutInMs);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      if (timer.current) clearTimeout(timer.current);
      throttledResetTimer.cancel();
      events.forEach((event) => {
        window.removeEventListener(event, throttledResetTimer, false);
      });
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [throttledResetTimer, timeoutInMs, logoutHandler]);
};
